import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, withStyles } from 'vopay';
import { BodyText, Card } from 'UI';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import styles from './StatusSummaryCard.module.scss';

const cn = withStyles(styles);

/**
 * A host that acts on a tile gets a radio group: only one status can be chosen at a time, and
 * choosing the chosen one again is how the host clears it. A host without one gets read-only tiles.
 */
type TileInteraction<Status extends string> =
  | {
      onStatusClick: (status: Status) => void;
      /** The status the host has chosen; its tile reads as selected. Empty for none. */
      selectedStatus: Status | '';
      /** Names the radio group for assistive tech: what choosing a tile does. */
      groupLabel: string;
    }
  | { onStatusClick?: undefined; selectedStatus?: undefined; groupLabel?: undefined };

type StatusSummaryCardProps<Status extends string> = TileInteraction<Status> & {
  fetching: boolean;
  numTotal: number;
  /** What the total counts, under the figure: "Accounts", "Partner Accounts". */
  totalLabel: string;
  /**
   * Partial while `fetching`; every documented status is present once the summary has responded.
   * The backend keys these off a live enum, so the response can also carry a status the portal has
   * no name for yet — it still counts toward "in onboarding".
   */
  statusCounts: { [status: string]: number | undefined };
  /** Statuses an entity holds once it has left (or been pulled out of) the onboarding pipeline. */
  terminalStatuses: ReadonlySet<string>;
  /** The tiles, in order. The host decides which statuses earn one (pipeline, populated, terminal…). */
  stages: readonly Status[];
  className?: string;
  'data-testid': string;
};

/**
 * A total, an active / in-onboarding legend and one tile per status, as the Accounts and Partner
 * Accounts pages show them (spec 5.2.2 of each). Which statuses get a tile, in which order, and what
 * a click does are the host's; the card only counts and renders.
 */
export default function StatusSummaryCard<Status extends string>({
  fetching,
  numTotal,
  totalLabel,
  statusCounts,
  terminalStatuses,
  stages,
  onStatusClick,
  selectedStatus,
  groupLabel,
  className,
  'data-testid': testId,
}: StatusSummaryCardProps<Status>) {
  const { t } = useTranslation('translations', { keyPrefix: 'UI.StatusSummaryCard' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();
  const radioGroupName = useId();

  const numActive = statusCounts.Active ?? 0;

  // Everything not terminal is somewhere in onboarding, a status the portal has no name for included.
  const numOnboarding = Object.entries(statusCounts).reduce(
    (sum, [status, count]) => (terminalStatuses.has(status) ? sum : sum + (count ?? 0)),
    0,
  );

  return (
    <Card variant="section" className={cn('card', className)} data-testid={testId}>
      <div className={cn('totalBlock')}>
        {fetching ? <Skeleton width={40} height={46} /> : <span className={cn('total')}>{numTotal}</span>}
        {/* Body/16/Regular, Text/Secondary75 */}
        <BodyText className={cn('secondaryText')}>{totalLabel}</BodyText>
      </div>

      <div className={cn('divider')} />

      <div className={cn('breakdown')}>
        {fetching ? (
          <Skeleton width={200} height={18} />
        ) : (
          <div className={cn('legend')}>
            {/* Body/13/Medium */}
            <BodyText size="sm" weight="md" className={cn('legendItem', 'legendActive')}>
              {t('active', { defaultValue: '{{count}} active', count: numActive })}
            </BodyText>

            <BodyText size="sm" weight="md" className={cn('legendItem', 'legendOnboarding')}>
              {t('inOnboarding', { defaultValue: '{{count}} in onboarding', count: numOnboarding })}
            </BodyText>
          </div>
        )}

        {onStatusClick ? (
          // Only one status can be chosen at a time, so the tiles are a radio group (the Accounts
          // page's ID-13981 pattern).
          <div role="radiogroup" aria-label={groupLabel} className={cn('tiles')}>
            {stages.map((stage) => {
              const count = statusCounts[stage] ?? 0;
              const isSelected = stage === selectedStatus;

              // A tile is a <label>, whose content must be phrasing, so BodyText's block <div>s can't
              // be used: its Body/13 text styles live in the module SCSS (same exception the
              // figma-to-vopay skill makes for option-card buttons).
              return (
                <label
                  key={stage}
                  className={cn('tile', 'tileInteractive', count === 0 && 'tileEmpty', isSelected && 'tileSelected')}
                  data-testid="onboardingStageTile"
                >
                  <input
                    type="radio"
                    name={radioGroupName}
                    value={stage}
                    checked={isSelected}
                    className={cn('tileRadio')}
                    onChange={() => onStatusClick(stage)}
                    // A checked radio fires no change when it is clicked (or Space-pressed) again, only
                    // a click, so that click is what lets the host clear it; an unchecked one is
                    // handled by onChange alone.
                    onClick={() => {
                      if (isSelected) onStatusClick(stage);
                    }}
                  />

                  <span className={cn('tileLabel')}>{resolveStatusLabel(stage)}</span>
                  {fetching ? <Skeleton width={12} height={18} /> : <span className={cn('tileCount')}>{count}</span>}
                </label>
              );
            })}
          </div>
        ) : (
          <div className={cn('tiles')}>
            {stages.map((stage) => {
              const count = statusCounts[stage] ?? 0;

              return (
                <div key={stage} className={cn('tile', count === 0 && 'tileEmpty')} data-testid="onboardingStageTile">
                  <span className={cn('tileLabel')}>{resolveStatusLabel(stage)}</span>
                  {fetching ? <Skeleton width={12} height={18} /> : <span className={cn('tileCount')}>{count}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
